"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { blockNameToAnchorId } from "@/blocks/block-utils";
import {
  scrollToElementBelowSiteHeader,
  syncSiteHeaderOffsetCssVar,
} from "@/utilities/scrollToSiteAnchor";

function findElementByIdCaseInsensitive(id: string): HTMLElement | null {
  const target = id.toLowerCase();

  for (const node of document.querySelectorAll<HTMLElement>("[id]")) {
    if (node.id.toLowerCase() === target) return node;
  }

  return null;
}

function scrollToBlockAnchorFromHash() {
  const raw = window.location.hash.slice(1);
  if (!raw) return;

  const anchorId = blockNameToAnchorId(decodeURIComponent(raw));
  if (!anchorId) return;

  const target = findElementByIdCaseInsensitive(anchorId);
  if (!target) return;

  scrollToElementBelowSiteHeader(target);
}

function handleInPageAnchorClick(event: MouseEvent) {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const anchor = (event.target as Element).closest<HTMLAnchorElement>(
    "a[href*='#']",
  );
  if (!anchor) return;

  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
    return;
  }

  const rawHash = url.hash.slice(1);
  if (!rawHash) return;

  const anchorId = blockNameToAnchorId(decodeURIComponent(rawHash));
  if (!anchorId) return;

  const target = findElementByIdCaseInsensitive(anchorId);
  if (!target) return;

  event.preventDefault();
  history.pushState(
    null,
    "",
    `${url.pathname}${url.search}#${decodeURIComponent(rawHash)}`,
  );
  scrollToElementBelowSiteHeader(target);
}

/** Case-insensitive `#blockName` scroll (å/ä→a, ö→o), offset below sticky header. */
export function BlockAnchorHashScroll() {
  const pathname = usePathname();

  useEffect(() => {
    syncSiteHeaderOffsetCssVar();

    const hasHash = Boolean(window.location.hash);
    if (hasHash) {
      history.scrollRestoration = "manual";
      window.scrollTo(0, 0);
    }

    const runScroll = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToBlockAnchorFromHash);
      });
    };

    runScroll();

    document.addEventListener("click", handleInPageAnchorClick, true);
    window.addEventListener("hashchange", runScroll);
    window.addEventListener("resize", syncSiteHeaderOffsetCssVar);

    return () => {
      document.removeEventListener("click", handleInPageAnchorClick, true);
      window.removeEventListener("hashchange", runScroll);
      window.removeEventListener("resize", syncSiteHeaderOffsetCssVar);
      history.scrollRestoration = "auto";
    };
  }, [pathname]);

  return null;
}
